import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@myhealth-raccoon/components';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { CodeRequestType } from 'src/services/auth';
import { verificationCodeService } from 'src/services/verificationCode';
import { useSelector } from 'src/store';
import { useCancelReservedBooking } from 'src/hooks/useCancelReservedBooking';
import { BackButton } from "src/components/BackButton";
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { ContentContainer } from 'src/components/ContentContainer';
import { captureException } from '@sentry/react';

const Checkbox = ({ title, value, checked, onClick, disabled }: { title: string, value?: string, checked?: boolean, onClick(): unknown, disabled?: boolean }) => {
    return <button className={classNames("border border-gray-200 rounded-lg p-4 flex gap-x-2 disabled:bg-gray-50", {
        "border-primary bg-primary-50": checked,
        "border-gray-200": !checked
    })} onClick={onClick} disabled={disabled}>
        {checked
            ? <i className='block w-4 h-4 rounded-full border-primary border bg-primary bg-clip-content p-1 mt-0.5' ></i>
            : disabled
                ? <i className='block w-4 h-4 rounded-full border-gray-300 border mt-0.5'></i>
                : <i className='block w-4 h-4 rounded-full border-gray-300 border mt-0.5'></i>
        }
        <div className={classNames('flex flex-col text-left', {
            "text-primary-700": checked,
            "text-gray-400": disabled
        })}>
            <span className={classNames('text-sm font-medium', {
                "text-primary-800": checked,
                "text-gray-500": disabled,
                "text-gray-800": !checked && !disabled,
            })}>{title}</span>
            <span className='text-sm'>{value}</span>
        </div>
    </button>
}

export function VerificationMethod() {
    useBookingFlowStep(1);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    useCancelReservedBooking();

    const { mobile: maskedMobile, email: maskedEmail } = useSelector(state => state.bookingFlow.verification) ?? {};
    const [codeSendType, setCodeSendType] = useState<CodeRequestType>(maskedEmail ? "Email" : "Mobile");

    const doSubmit = useCallback(async () => {
        if (!codeSendType) {
            return;
        }
        setIsLoading(true);
        try {
            await verificationCodeService.GenerateCode(codeSendType);
            setIsLoading(false);
            navigate(`/app/verification/${codeSendType === "Mobile" ? "sms" : "email"}`);
        } catch (e) {
            captureException(e);
            if (e instanceof Error) {
                toast.error(e.message || e.name);
            }
            setIsLoading(false);
        }
    }, [codeSendType, navigate]);

    useEffect(() => {
        if (!maskedMobile) {
            navigate(-1);
        }
    }, [maskedMobile, navigate]);

    return <>
        <ContentContainer className='flex-1 lg:flex-none'>
            <section className='flex flex-col gap-y-2 py-5'>
                <h1 className='text-2xl font-semibold text-gray-900'>Verify your identity</h1>
                <p>Where would you like us to send your security code?</p>
            </section>
            <section className='flex flex-col gap-y-5 py-5'>
                <Checkbox
                    disabled={!maskedEmail}
                    title='Email me'
                    value={maskedEmail || "Not provided"}
                    checked={codeSendType === "Email"}
                    onClick={() => setCodeSendType("Email")}
                />
                <Checkbox
                    title='Text me'
                    value={maskedMobile}
                    checked={codeSendType === "Mobile"}
                    onClick={() => setCodeSendType("Mobile")}
                />
            </section>
        </ContentContainer>
        <StickyBottomBar>
            <BackButton
                onClick={() => navigate(-1)}
                label="Back" />
            <Button className='flex-1 text-center'
                disabled={!codeSendType || isLoading}
                onClick={doSubmit}>Next</Button>
        </StickyBottomBar>
    </>
}